import { useState, useEffect, useMemo } from 'react'
import { Heart, Reply, Edit2, Trash2 } from 'lucide-react'
import { useAuthStore } from '@/stores/authStore'
import { postsService, PostComment } from '@/services/postsService'
import { ConfirmDialog } from '@/components/ui/ConfirmDialog'

interface CommentSectionProps {
  postId: string
  onCommentCountChange?: (delta: number) => void
}

export function CommentSection({ postId, onCommentCountChange }: CommentSectionProps) {
  const { user } = useAuthStore()
  const [comments, setComments] = useState<PostComment[]>([])
  const [loading, setLoading] = useState(true)
  const [newComment, setNewComment] = useState('')
  const [replyingTo, setReplyingTo] = useState<string | null>(null)
  const [replyContent, setReplyContent] = useState('')
  const [editingComment, setEditingComment] = useState<string | null>(null)
  const [editContent, setEditContent] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [likedComments, setLikedComments] = useState<Map<string, boolean>>(new Map())

  // ConfirmDialog state
  const [confirmOpen, setConfirmOpen] = useState(false)
  const [pendingDeleteId, setPendingDeleteId] = useState<string | null>(null)

  // Memoizar comentários ordenados para evitar recriação do array a cada render
  const sortedComments = useMemo(() => {
    return [...comments].sort((a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime())
  }, [comments])

  useEffect(() => {
    loadComments()
  }, [postId, user])

  const loadComments = async () => {
    try {
      setLoading(true)
      const response = await postsService.getComments(postId, { limit: 100 })
      const commentsData = response.data || []
      setComments(commentsData)

      if (user && commentsData.length > 0) {
        const likesMap = new Map<string, boolean>()
        await Promise.all(
          commentsData.map(async (comment: PostComment) => {
            try {
              const likedData = await postsService.checkCommentLiked(postId, comment.id)
              likesMap.set(comment.id, likedData.liked)
            } catch (err) {
              likesMap.set(comment.id, false)
            }
          })
        )
        setLikedComments(likesMap)
      }
    } catch (err) {
      console.error('Error loading comments:', err)
    } finally {
      setLoading(false)
    }
  }

  const handleAddComment = async () => {
    if (!newComment.trim() || !user) return
    try {
      setSubmitting(true)
      const response = await postsService.addComment(postId, newComment)
      setNewComment('')
      const newCommentData: PostComment = {
        id: response.comment.id,
        content: newComment,
        parent_id: null,
        user_id: user.id,
        user_name: user.name,
        user_avatar: user.avatarUrl || user.avatar || null,
        like_count: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }
      setComments(prev => [newCommentData, ...prev])
      setLikedComments(prev => new Map(prev).set(response.comment.id, false))
      onCommentCountChange?.(+1)
    } catch (err) {
      alert('Erro ao adicionar comentário')
    } finally {
      setSubmitting(false)
    }
  }

  const handleReply = async (parentId: string) => {
    if (!replyContent.trim() || !user) return
    try {
      setSubmitting(true)
      const response = await postsService.addComment(postId, replyContent, parentId)
      setReplyContent('')
      setReplyingTo(null)
      const newReplyData: PostComment = {
        id: response.comment.id,
        content: replyContent,
        parent_id: parentId,
        user_id: user.id,
        user_name: user.name,
        user_avatar: user.avatarUrl || user.avatar || null,
        like_count: 0,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString(),
      }
      setComments(prev => [...prev, newReplyData])
      setLikedComments(prev => new Map(prev).set(response.comment.id, false))
      onCommentCountChange?.(+1)
    } catch (err: any) {
      if (err?.response?.status === 401) {
        alert('Sessão expirou. Faça login novamente.')
        window.location.href = '/login'
      } else {
        alert('Erro ao adicionar resposta')
      }
    } finally {
      setSubmitting(false)
    }
  }

  const handleEdit = async (commentId: string) => {
    if (!editContent.trim()) return
    try {
      setSubmitting(true)
      await postsService.updateComment(postId, commentId, editContent)
      setEditingComment(null)
      setEditContent('')
      await loadComments()
    } catch (err) {
      alert('Erro ao editar comentário')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = (commentId: string) => {
    setPendingDeleteId(commentId)
    setConfirmOpen(true)
  }

  const handleConfirmDelete = async () => {
    setConfirmOpen(false)
    if (!pendingDeleteId) return
    try {
      await postsService.deleteComment(postId, pendingDeleteId)
      await loadComments()
      onCommentCountChange?.(-1)
    } catch (err) {
      console.error('Erro ao deletar comentário', err)
    } finally {
      setPendingDeleteId(null)
    }
  }

  const handleLike = async (commentId: string) => {
    if (!user) {
      alert('Faça login para curtir')
      return
    }
    const isLiked = likedComments.get(commentId) || false
    try {
      if (isLiked) {
        await postsService.unlikeComment(postId, commentId)
      } else {
        await postsService.likeComment(postId, commentId)
      }
      setLikedComments(prev => new Map(prev).set(commentId, !isLiked))
      const response = await postsService.getComments(postId, { limit: 100 })
      setComments(response.data || [])
    } catch (err: any) {
      if (err?.response?.status === 401) {
        alert('Sessão expirou. Faça login novamente.')
        window.location.href = '/login'
      }
    }
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    const now = new Date()
    const diffInHours = Math.floor((now.getTime() - date.getTime()) / (1000 * 60 * 60))
    if (diffInHours < 1) return 'agora'
    if (diffInHours < 24) return `${diffInHours}h`
    const diffInDays = Math.floor(diffInHours / 24)
    if (diffInDays < 7) return `${diffInDays}d`
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short' })
  }

  const getInitials = (name: string) => {
    return name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
  }

  const renderComment = (comment: PostComment): JSX.Element => {
    const isOwner = user?.id === comment.user_id
    const isEditing = editingComment === comment.id
    const parentComment = comment.parent_id ? comments.find(c => c.id === comment.parent_id) : null

    return (
      <div className="py-3 hover:bg-brand-surface-alt rounded-xl transition-colors">
        <div className="flex gap-3">
          <div className="flex-shrink-0">
            {comment.user_avatar ? (
              <img
                src={comment.user_avatar}
                alt={comment.user_name}
                className="w-9 h-9 rounded-full object-cover"
                onError={(e) => {
                  e.currentTarget.style.display = 'none'
                  e.currentTarget.nextElementSibling?.classList.remove('hidden')
                }}
              />
            ) : null}
            <div className={`w-9 h-9 rounded-full bg-brand-primary text-white dark:text-verde-500 flex items-center justify-center font-bold text-xs ${comment.user_avatar ? 'hidden' : ''}`}>
              {getInitials(comment.user_name)}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-baseline gap-1.5 mb-1">
              <span className="font-bold text-brand-text text-sm">{comment.user_name}</span>
              <span className="text-xs text-brand-text-muted">· {formatDate(comment.created_at)}</span>
              {comment.updated_at !== comment.created_at && <span className="text-xs text-brand-text-muted opacity-60">· editado</span>}
            </div>
            {isEditing ? (
              <div className="space-y-2 mb-2">
                <textarea value={editContent} onChange={(e) => setEditContent(e.target.value)} className="w-full px-3 py-2 input-field text-sm" rows={3} maxLength={1000} />
                <div className="flex gap-2">
                  <button onClick={() => handleEdit(comment.id)} disabled={submitting} className="px-3 py-1.5 bg-brand-primary text-white dark:text-verde-500 rounded-lg text-xs">Salvar</button>
                  <button onClick={() => setEditingComment(null)} className="px-3 py-1.5 bg-brand-surface-alt text-brand-text-secondary rounded-lg text-xs">Cancelar</button>
                </div>
              </div>
            ) : (
              <>
                {parentComment && (
                  <div className="mb-2 pl-3 border-l-4 border-brand-accent bg-brand-primary-light rounded-lg py-2 px-3">
                    <div className="text-xs font-semibold text-brand-primary mb-0.5">{parentComment.user_name}</div>
                    <div className="text-xs text-brand-text-secondary line-clamp-2">{parentComment.content}</div>
                  </div>
                )}
                <p className="text-brand-text text-sm mb-2 break-words">{comment.content}</p>
              </>
            )}
            <div className="flex items-center gap-4 text-xs text-brand-text-muted">
              <button onClick={() => handleLike(comment.id)} className={`flex items-center gap-1.5 hover:text-red-600 transition-colors ${likedComments.get(comment.id) ? 'text-red-600' : ''}`}>
                <Heart className={`w-4 h-4 ${likedComments.get(comment.id) ? 'fill-current' : ''}`} />
                <span className="font-medium">{comment.like_count || 0}</span>
              </button>
              {user && <button onClick={() => setReplyingTo(replyingTo === comment.id ? null : comment.id)} className="flex items-center gap-1.5 hover:text-brand-primary transition-colors"><Reply className="w-4 h-4" /><span className="font-medium">Responder</span></button>}
              {isOwner && !isEditing && (
                <>
                  <button onClick={() => { setEditingComment(comment.id); setEditContent(comment.content) }} className="flex items-center gap-1.5 hover:text-blue-600 transition-colors"><Edit2 className="w-4 h-4" /><span className="font-medium">Editar</span></button>
                  <button onClick={() => handleDelete(comment.id)} className="flex items-center gap-1.5 hover:text-red-600 transition-colors"><Trash2 className="w-4 h-4" /><span className="font-medium">Deletar</span></button>
                </>
              )}
            </div>
            {replyingTo === comment.id && (
              <div className="mt-3 bg-brand-surface-alt rounded-xl p-3">
                <textarea value={replyContent} onChange={(e) => setReplyContent(e.target.value)} placeholder="Escreva sua resposta..." className="w-full px-3 py-2 input-field text-sm resize-none" rows={2} maxLength={1000} />
                <div className="flex justify-end gap-2 mt-2">
                  <button onClick={() => { setReplyingTo(null); setReplyContent('') }} className="px-4 py-1.5 text-brand-text-secondary text-xs font-medium hover:text-brand-text">Cancelar</button>
                  <button onClick={() => handleReply(comment.id)} disabled={submitting || !replyContent.trim()} className="px-4 py-1.5 bg-brand-primary text-white dark:text-verde-500 rounded-lg text-xs font-medium hover:bg-brand-primary-hover disabled:opacity-50 disabled:cursor-not-allowed">Responder</button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="mt-12 border-t border-brand-border pt-8">
      <ConfirmDialog
        open={confirmOpen}
        title="Deletar comentário"
        message="Deletar este comentário? Esta ação não pode ser desfeita."
        confirmLabel="Deletar"
        variant="danger"
        onConfirm={handleConfirmDelete}
        onCancel={() => setConfirmOpen(false)}
      />
      <h2 className="text-xl font-bold text-brand-text mb-6">Comentários ({comments.length})</h2>
      {user ? (
        <div className="mb-8">
          <textarea value={newComment} onChange={(e) => setNewComment(e.target.value)} placeholder="Compartilhe sua opinião..." className="w-full px-4 py-3 input-field" rows={4} maxLength={1000} />
          <div className="flex justify-between items-center mt-2">
            <span className="text-sm text-brand-text-muted">{newComment.length}/1000</span>
            <button onClick={handleAddComment} disabled={submitting || !newComment.trim()} className="btn-primary disabled:opacity-50">{submitting ? 'Enviando...' : 'Comentar'}</button>
          </div>
        </div>
      ) : (
        <div className="mb-8 p-4 bg-brand-surface-alt rounded-xl text-center"><p className="text-brand-text-secondary text-sm">Faça login para participar da discussão</p></div>
      )}
      {loading ? (
        <div className="text-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-2 border-brand-primary border-t-transparent mx-auto"></div></div>
      ) : comments.length === 0 ? (
        <div className="text-center py-8 text-brand-text-muted">Seja o primeiro a comentar!</div>
      ) : (
        <div className="space-y-2">
          {sortedComments.map(comment => (
            <div key={comment.id}>
              {renderComment(comment)}
            </div>
          ))}
        </div>
      )}
    </div>
  )
}
